import React, { useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import translations from './myorganization.psa.i18n';

import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import { useLoader } from '../api/useLoader';
import Spinner from '../components/spinner/spinner';

import { HttpError } from '../api/generated/studiested-generated-api';
import { ConfirmationDialog } from '../components/dialog';
import { uuidv4 } from '../util/uuidv4';
import {
  AlpNotificationType,
  NotificationFunctionContext,
} from '../components/notifications/notificationProvider';
import UserTablePs from './usertable.ps';

const Colleagues = () => {
  const { brukerApi, praksisstedApi } = useContext(PraksisstedApiContext);
  const { addNotification } = useContext(NotificationFunctionContext);
  const { data, loading, reload } = useLoader(
    () => praksisstedApi.praksisstedbrukereGet(),
    [],
  );

  type IUserDeleteConfirmation =
    | { open: false }
    | {
        open: true;
        praksisstedbrukerId: string;
        name: string;
        numActivities: number;
      };
  const [deleteConfirmation, setDeleteConfirmation] =
    useState<IUserDeleteConfirmation>({ open: false });

  const { formatMessage } = useIntl();
  const labels = {
    title: formatMessage({ id: translations.title.id }),
    subtitle: formatMessage({ id: translations.subtitle.id }),
    confirmDeleteTitle: formatMessage({
      id: translations.confirmDeleteTitle.id,
    }),
    deleteSucceeded: formatMessage({ id: translations.deleteSucceeded.id }),
  };

  const handleDelete = (id: string, name: string) => {
    brukerApi
      .praksisaktiviteterIdGet({ pathParams: { id: id } })
      .then((aktiviteter) => {
        const numActivities = aktiviteter.fremtidige
          ? aktiviteter.fremtidige.length
          : 0;
        setDeleteConfirmation({
          open: true,
          praksisstedbrukerId: id,
          name: name,
          numActivities: numActivities,
        });
      })
      .catch((err: HttpError) => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: err.body,
        });
      });
  };

  const doDelete = (ok: boolean) => {
    if (!deleteConfirmation.open) return;

    setDeleteConfirmation({ open: false });

    if (!ok) return; // the user chose to abort

    praksisstedApi
      .praksisstedbrukerIdDelete({
        pathParams: { id: deleteConfirmation.praksisstedbrukerId },
      })
      .then(() => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.SUCCESS,
          message: labels.deleteSucceeded,
        });
        reload();
      })
      .catch((err: HttpError) => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: err.body,
        });
      });
  };

  const confirmationText = () => {
    if (deleteConfirmation.open) {
      return (
        <>
          {deleteConfirmation.numActivities > 0 ? (
            <>
              <p>
                {formatMessage(
                  { id: translations.confirmDeleteUserWithActivities1.id },
                  {
                    name: deleteConfirmation.open && deleteConfirmation.name,
                  },
                )}
              </p>
              <p>
                {formatMessage(
                  { id: translations.confirmDeleteUserWithActivities2.id },
                  {
                    numActivities:
                      deleteConfirmation.open &&
                      deleteConfirmation.numActivities,
                  },
                )}
              </p>
            </>
          ) : (
            <p>
              {formatMessage(
                { id: translations.confirmDeleteUser.id },
                {
                  name: deleteConfirmation.open && deleteConfirmation.name,
                },
              )}
            </p>
          )}
        </>
      );
    }
    return '';
  };

  return (
    <>
      {loading && <Spinner />}
      {data && (
        <>
          <ConfirmationDialog
            isOpen={deleteConfirmation.open}
            onClose={doDelete}
            title={labels.confirmDeleteTitle}
          >
            {confirmationText()}
          </ConfirmationDialog>
          <UserTablePs
            users={data.filter((bruker) => bruker.fornavn !== 'SYSTEM')}
            handleDelete={handleDelete}
          />
        </>
      )}
    </>
  );
};

export default Colleagues;
